import { useQuery } from '@apollo/client';
import { Router, useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import styled from 'styled-components';
import { TidalButton } from '../../../components/common/button';
import { TidalDropdownIcon } from '../../../components/common/dropdown';
import TidalModal from '../../../components/common/modal/TidalModal';
import GraphQLErrorText from '../../../components/GraphQLErrorText';
import Loading from '../../../components/Loading';
import TidalDashboardHeader from '../../../components/tidal-dashboard/common/header';
import TidalDashboardAddNewServerModal from '../../../components/tidal-dashboard/servers/TidalDashboardAddNewServerModal';
import TidalDashboardServerCard from '../../../components/tidal-dashboard/servers/TidalDashboardServerCard';
import TidalDashboardServerCreatedModal from '../../../components/tidal-dashboard/servers/TidalDashboardServerCreatedModal';
import useAuthOrRedirect from '../../../hooks/useAuthOrRedirect';
import { PROFILE } from '../../../lib/graphql/queries';
import TidalDashboardServerSnapshotCard from '../../../components/tidal-dashboard/servers/TidalDashboardServerSnapshotCard';
import useInterval from '../../../hooks/useInterval';

const TidalDashboardServersWrapper = styled.div`
  padding-bottom: 100px;
  width: 100%;
`;

const TidalDashboardContainer = styled.div`
  padding: 20px 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(317px, 1fr));
  grid-gap: 24px;
  @media only screen and (max-width: 1100px) {
    grid-gap: 16px;
  }
  @media only screen and (max-width: 540px) {
    grid-gap: 10px;
    grid-template-columns: 1fr;
  }
`;

const TidalDashboardServersHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 307px 189px;
  grid-gap: 8px 19px;
  margin-top: 22px;
  align-items: center;

  @media only screen and (max-width: 1110px) {
    grid-template-columns: 1fr 100px 150px 150px;
  }
  @media only screen and (max-width: 660px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;
  }
`;

const TidalDashboardServersHeaderTitle = styled.p`
  font-weight: 700;
  font-size: 21px;
  line-height: 25px;
  color: ${(props) => props.theme.colors.text[1]};
`;

const TidalDashboardServers = (): JSX.Element => {
  const { loading, error } = useQuery(PROFILE);

  const {
    data,
    error: err,
    isValidating,
    mutate,
  } = useSWR('/dashboard/servers', {
    fallbackData: [],
  });

  const {
    data: snapshots,
    error: snapshotErr,
    mutate: snapshotMutate,
  } = useSWR('/dashboard/servers/snapshots');

  const router = useRouter();

  const [isServerCreatedModalVisible, setIsServerCreatedModalVisible] =
    useState(false);
  const [isCreateServerModalVisible, SetIsCreateServerModalVisible] =
    useState(false);
  const [serversReady, setServersReady] = useState(false);
  const [createdResponse, setCreatedResponse] = useState([]);
  const [activeMenuIndex, setActiveMenuIndex] = useState(0);
  const [filteredServers, setFilteredServers] = useState(data);

  useEffect(() => {
    if (data && data.length > 0 && typeof data == 'object') {
      setFilteredServers(data);
    } else {
      setFilteredServers([]);
    }
  }, [data]);

  const { activeServersCount, inActiveServersCount } = useMemo(() => {
    return {
      activeServersCount: (Array.isArray(data) ? data : []).filter(
        (d) => d.state == 'active',
      ).length,
      inActiveServersCount: (Array.isArray(data) ? data : []).filter(
        (d) => d.state == 'stopped',
      ).length,
    };
  }, [data]);

  // run snapshot mutate every 1 minute if there is a snapshot in creating state
  useInterval(
    () => {
      snapshotMutate(snapshots);
    },
    (Array.isArray(snapshots) ? snapshots : []).filter(
      (snapshot) => snapshot.state === 'creating',
    ).length > 0
      ? 1000 * 60 * 5
      : null,
  );

  const handleFilterServers = useCallback(
    (type) => {
      if (type == 'all') {
        setFilteredServers(data);
        return;
      }
      const temp = data.filter((server) => server.state == type);
      setFilteredServers(temp);
    },
    [data],
  );

  const handleOpenGuides = () => {
    window.open(
      'https://guides.tidalmarket.com/server-generator-guide/creating-servers',
      '_server_guides',
    );
  };

  if (!useAuthOrRedirect()) return null;
  if (loading || (!err && !data)) return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;

  return (
    <TidalDashboardServersWrapper>
      <TidalModal
        isOpen={isServerCreatedModalVisible}
        onClose={() => {
          if (serversReady) {
            setIsServerCreatedModalVisible(false);
            mutate(data);
          }
        }}
        Modal={
          <TidalDashboardServerCreatedModal
            response={createdResponse}
            onServersReady={() => {
              setServersReady(true);
            }}
            onClose={() => {
              if (serversReady) {
                setIsServerCreatedModalVisible(false);
                mutate(data);
              }
            }}
          />
        }
      />

      <TidalModal
        isOpen={isCreateServerModalVisible}
        onClose={() => {
          SetIsCreateServerModalVisible(false);
        }}
        Modal={
          <TidalDashboardAddNewServerModal
            onClose={() => {
              SetIsCreateServerModalVisible(false);
            }}
            onCreated={(data) => {
              setCreatedResponse(data);
              setIsServerCreatedModalVisible(true);
              mutate(data);
            }}
            snapshots={snapshots}
          />
        }
      />
      <TidalDashboardHeader
        title="Servers"
        key="serverHeader"
        showSettings={true}
        onSettingsClick={() => {
          router.push('/tidal-dashboard/servers/settings');
        }}
        tags={[
          {
            text: `${data?.length || 0} All`,
            isActive: activeMenuIndex == 0,
          },
          {
            text: `${activeServersCount} Active`,
            isActive: activeMenuIndex == 1,
          },
          {
            text: `${inActiveServersCount} Stopped`,
            isActive: activeMenuIndex == 2,
          },
          {
            text: `${snapshots?.length || 0} Saved`,
            isActive: activeMenuIndex == 3,
          },
        ]}
        onTagClick={(index) => {
          setActiveMenuIndex(index);
          handleFilterServers(
            index == 1 ? 'active' : index == 0 ? 'all' : 'stopped',
          );
        }}
      />
      <TidalDashboardServersHeader>
        <TidalDashboardServersHeaderTitle>
          Showing{' '}
          {activeMenuIndex === 3 ? snapshots?.length : filteredServers?.length}{' '}
          {activeMenuIndex === 3 ? 'snapshot' : 'server'}
          {filteredServers?.length !== 1 ? 's' : ''}
        </TidalDashboardServersHeaderTitle>
        <TidalButton
          type="default"
          text="Guides"
          onClick={handleOpenGuides}
          tint={false}
        />
        <TidalDropdownIcon
          onSelect={(item) => {
            switch (item) {
              case 'All servers':
                setActiveMenuIndex(0);
                handleFilterServers('all');
                break;
              case 'Active':
                setActiveMenuIndex(1);
                handleFilterServers('active');
                break;
              case 'Inactive':
                setActiveMenuIndex(2);
                handleFilterServers('stopped');
                break;
            }
          }}
          defaultValue={
            activeMenuIndex == 0
              ? 'All servers'
              : activeMenuIndex == 1
              ? 'Active'
              : 'Inactive'
          }
          label="All Servers"
          items={['All servers', 'Active', 'Inactive']}
        />
        <TidalButton
          type="info"
          text="Generate new"
          tint={false}
          onClick={() => {
            setServersReady(false);
            SetIsCreateServerModalVisible(true);
          }}
        />
      </TidalDashboardServersHeader>
      <TidalDashboardContainer>
        {activeMenuIndex === 3
          ? snapshots?.map((snapshot, index) => (
              <TidalDashboardServerSnapshotCard
                key={`server-snapshot-${snapshot.id}`}
                name={snapshot.name}
                region={snapshot.region}
                provider={snapshot.provider}
                state={snapshot.state}
                id={snapshot.id}
                updatedAt={snapshot.updatedAt}
                onUpdate={() => {
                  snapshotMutate(snapshots);
                }}
              />
            ))
          : filteredServers?.map((server, index) => (
              <TidalDashboardServerCard
                key={`server-${server.id}`}
                name={server.name}
                region={server.region}
                os={server.operatingSystem}
                image={server.image}
                provider={server.provider}
                serverType={server?.serverType}
                status={server.state}
                ipAddress={server.ipAddress}
                instanceId={server.instanceId}
                id={server.id}
                updatedAt={server.updatedAt}
                onUpdate={() => {
                  mutate(data);
                  snapshotMutate(snapshots);
                }}
              />
            ))}
      </TidalDashboardContainer>
    </TidalDashboardServersWrapper>
  );
};

export default TidalDashboardServers;
